import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Card } from '@clari/ui'
import { AccountHealthBadge } from '@/components/banking/AccountHealthBadge'
import { bankingApi } from '@/lib/banking-api'
import type { ReviewDto } from '@/lib/types'

const SUPPORT_MAILTO = 'mailto:support@withclari.com'

function fmtDate(iso: string | null, locale: string): string {
  return iso ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso)) : '—'
}

export function AccountHealth() {
  const { t, i18n } = useTranslation('banking')
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => bankingApi.listAccounts() })
  const account = accountsQuery.data?.items[0]
  const healthQuery = useQuery({
    queryKey: ['health', account?.id],
    queryFn: () => bankingApi.getHealth(account?.id ?? ''),
    enabled: Boolean(account),
  })

  function ReviewRow({ review }: { review: ReviewDto }) {
    return (
      <li className="border-b border-border py-3 last:border-0">
        <p className="text-sm font-medium">
          {t(`health.review_type.${review.type}`, { defaultValue: review.type })}
        </p>
        {review.reason_public ? (
          <p className="text-sm text-text-muted">{review.reason_public}</p>
        ) : null}
        <p className="mt-1 text-xs text-text-subtle">
          {t('health.started')} {fmtDate(review.started_at, i18n.language)}
        </p>
        {review.estimated_resolution_at ? (
          <p className="text-xs text-text-subtle">
            {t('health.eta')} {fmtDate(review.estimated_resolution_at, i18n.language)}
          </p>
        ) : null}
        {review.escalated_at ? (
          <p className="mt-1 text-xs text-warning">{t('health.escalated')}</p>
        ) : null}
      </li>
    )
  }

  return (
    <div className="mx-auto flex max-w-xl flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('health.title')}</h1>
        <p className="mt-1 text-text-muted">{t('health.subtitle')}</p>
      </header>

      {healthQuery.isLoading ? <p className="text-text-muted">{t('common.loading')}</p> : null}

      {healthQuery.data ? (
        <>
          <Card>
            <AccountHealthBadge indicator={healthQuery.data.indicator} />
          </Card>

          <Card>
            <h2 className="mb-2 text-lg font-medium">{t('health.open_reviews')}</h2>
            {healthQuery.data.open_reviews.length === 0 ? (
              <p className="text-sm text-text-muted">{t('health.no_reviews')}</p>
            ) : (
              <ul className="flex flex-col">
                {healthQuery.data.open_reviews.map((r) => (
                  <ReviewRow key={r.id} review={r} />
                ))}
              </ul>
            )}
            <a
              href={SUPPORT_MAILTO}
              className="mt-4 inline-block rounded-md border border-border px-4 py-2 text-sm font-medium text-text"
            >
              {t('health.talk_to_human')}
            </a>
          </Card>

          {healthQuery.data.history.length > 0 ? (
            <Card>
              <h2 className="mb-2 text-lg font-medium">{t('health.history')}</h2>
              <ul className="flex flex-col">
                {healthQuery.data.history.map((r) => (
                  <ReviewRow key={r.id} review={r} />
                ))}
              </ul>
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  )
}
